import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { Listing, ListingKind, MyBazaar as MyBazaarData, Sellable } from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Modal, Panel } from '../components/ui';
import { ListingCard } from '../components/ListingCard';
import { EggArt } from '../components/EggArt';
import { formatNumber, timeAgo } from '../lib/format';

/** The player's own shop: what is out on the table, and what could be. */
export function MyBazaarPage() {
  const { me, refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<MyBazaarData>(() => api.get('/bazaar/mine'));
  const [listing, setListing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function cancel(row: Listing) {
    setBusy(row.id);
    try {
      await api.post(`/bazaar/listings/${row.id}/cancel`, {});
      push('info', 'Listing taken down — everything is back in your collection.');
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 380 }} />;

  const { bazaar, listings, slots, recentSales, totalSpent, fee } = state.data;
  const earned = recentSales.reduce((sum, sale) => sum + sale.total - sale.fee, 0);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <Link className="tiny muted" to="/bazaar">
            ← The Bazaar
          </Link>
          <h1 className="page-head__title">{bazaar.name}</h1>
          <div className="page-head__sub">
            {bazaar.greeting || 'Set out whatever you can spare. You choose the price.'}
          </div>
        </div>
        <Button variant="go" size="lg" disabled={slots.free <= 0} onClick={() => setListing(true)}>
          {slots.free > 0 ? 'Sell something' : 'Bazaar full'}
        </Button>
      </div>

      <div className={`pxframe pxframe--raised`} style={{ overflow: 'hidden' }}>
        <div className={`awning awning--${bazaar.awningStyle}`} aria-hidden />
        <div className="shopfront__head">
          <div className="row" style={{ gap: 'var(--gap-lg)' }}>
            <span className="shop-stat">
              <span className="shop-stat__label">SOLD</span>
              {formatNumber(bazaar.totalSales)}
            </span>
            <span className="shop-stat">
              <span className="shop-stat__label">EARNED</span>
              {formatNumber(bazaar.totalRevenue)}
            </span>
            <span className="shop-stat">
              <span className="shop-stat__label">SPENT</span>
              {formatNumber(totalSpent)}
            </span>
          </div>
          <div className="stack-sm" style={{ gap: 2, minWidth: 170 }}>
            <div className="tiny muted mono">
              {slots.used} / {slots.total} listing slots
            </div>
            <Bar ratio={slots.used / Math.max(1, slots.total)} />
            <div className="tiny muted">Sellers pay a {fee}% fee on each sale.</div>
          </div>
        </div>
      </div>

      <Panel title="◈ Out on the table" hint={`${listings.length} listed`}>
        {listings.length === 0 ? (
          <EmptyState art="⛁" title="Nothing for sale yet">
            Spare Pokémon, eggs you won't hatch and duplicate items are all worth something to
            somebody.
          </EmptyState>
        ) : (
          <div className="stall-grid">
            {listings.map((row) => (
              <ListingCard
                key={row.id}
                listing={row}
                busy={busy === row.id}
                showSeller={false}
                onCancel={(target) => void cancel(target)}
              />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="⛁ Recent sales" raised={false}>
        {recentSales.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>
            Nothing sold yet.
          </p>
        ) : (
          <div className="stack-sm">
            {recentSales.map((sale) => (
              <div key={sale.id} className="row row--between tiny">
                <span className="mono">
                  {sale.quantity > 1 ? `${sale.quantity}x ` : ''}
                  {sale.kind.toLowerCase()}
                </span>
                <span className="muted">
                  +{formatNumber(sale.total - sale.fee)} (fee {formatNumber(sale.fee)}) ·{' '}
                  {timeAgo(sale.createdAt)}
                </span>
              </div>
            ))}
            <div className="row row--between tiny mono" style={{ marginTop: 4 }}>
              <strong>Recent take</strong>
              <strong>{formatNumber(earned)}</strong>
            </div>
          </div>
        )}
      </Panel>

      {listing && me && (
        <SellModal
          durations={state.data.durations}
          fee={fee}
          onClose={() => setListing(false)}
          onListed={() => {
            setListing(false);
            state.reload();
            void refresh();
          }}
        />
      )}
    </div>
  );
}

/* --- Listing something ---------------------------------------------------- */

function SellModal({
  durations,
  fee,
  onClose,
  onListed,
}: {
  durations: number[];
  fee: number;
  onClose: () => void;
  onListed: () => void;
}) {
  const { push } = useToast();
  const sellable = useAsync<Sellable>(() => api.get('/bazaar/sellable'));
  const [kind, setKind] = useState<ListingKind>('POKEMON');
  const [picked, setPicked] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(1000);
  const [days, setDays] = useState(durations[2] ?? 7);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const stock =
    kind === 'ITEM' ? sellable.data?.items.find((row) => row.itemKey === picked)?.quantity ?? 1 : 1;
  const total = price * (kind === 'ITEM' ? quantity : 1);
  const take = total - Math.floor((total * fee) / 100);

  async function list() {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post('/bazaar/listings', {
        kind,
        pokemonId: kind === 'POKEMON' ? picked : undefined,
        eggId: kind === 'EGG' ? picked : undefined,
        itemKey: kind === 'ITEM' ? picked : undefined,
        quantity: kind === 'ITEM' ? quantity : undefined,
        unitPrice: price,
        durationDays: days,
        note: note.trim() || undefined,
      });
      push('success', "It's out on the table.", '⛁');
      onListed();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not list that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="sell-title" wide>
      <div className="stack">
        <div>
          <h2 id="sell-title">Sell something</h2>
          <p className="tiny muted" style={{ marginTop: 4 }}>
            While it is listed it stays in your Bazaar — out of your Grove, the Nursery and any
            trade — until it sells, you take it down, or it expires.
          </p>
        </div>

        <div className="row row--wrap" style={{ gap: 4 }}>
          {(['POKEMON', 'EGG', 'ITEM'] as ListingKind[]).map((option) => (
            <Button
              key={option}
              size="sm"
              variant={kind === option ? 'primary' : 'default'}
              onClick={() => {
                setKind(option);
                setPicked(null);
                setQuantity(1);
              }}
            >
              {option === 'POKEMON' ? 'Pokémon' : option === 'EGG' ? 'Eggs' : 'Items'}
            </Button>
          ))}
        </div>

        {sellable.loading ? (
          <div className="pxwell skeleton" style={{ height: 160 }} />
        ) : (
          <div className="sell-grid">
            {kind === 'POKEMON' &&
              (sellable.data?.pokemon ?? []).map((mon) => (
                <button
                  key={mon.id}
                  type="button"
                  className={`pxframe sell-card ${picked === mon.id ? 'sell-card--picked' : ''}`}
                  onClick={() => setPicked(mon.id)}
                >
                  <img className="pixel" src={mon.sprite} alt="" loading="lazy" />
                  <div className="tiny mono">{mon.displayName}</div>
                  <div className="tiny muted">Lv {mon.level}</div>
                </button>
              ))}

            {kind === 'EGG' &&
              (sellable.data?.eggs ?? []).map((egg) => (
                <button
                  key={egg.id}
                  type="button"
                  className={`pxframe sell-card ${picked === egg.id ? 'sell-card--picked' : ''}`}
                  onClick={() => setPicked(egg.id)}
                >
                  <EggArt appearance={egg.appearance} size="xs" state="still" label={egg.familyName} />
                  <div className="tiny mono">{egg.familyName}</div>
                  <div className="tiny muted">
                    {egg.progress}/{egg.requiredProgress}
                  </div>
                </button>
              ))}

            {kind === 'ITEM' &&
              (sellable.data?.items ?? []).map((item) => (
                <button
                  key={item.itemKey}
                  type="button"
                  className={`pxframe sell-card ${picked === item.itemKey ? 'sell-card--picked' : ''}`}
                  onClick={() => {
                    setPicked(item.itemKey);
                    setQuantity(1);
                  }}
                >
                  <div style={{ fontSize: '1.8rem' }} aria-hidden>
                    ◆
                  </div>
                  <div className="tiny mono">{item.name}</div>
                  <div className="tiny muted">x{item.quantity}</div>
                </button>
              ))}
          </div>
        )}

        {!picked && (
          <p className="tiny muted center" style={{ margin: 0 }}>
            Pick something to sell.
          </p>
        )}

        <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
          <div className="stack-sm">
            <span className="field__label">Price each</span>
            <input
              className="input"
              type="number"
              min={1}
              value={price}
              onChange={(event) => setPrice(Math.max(1, Number(event.target.value)))}
              style={{ width: 140 }}
            />
          </div>
          {kind === 'ITEM' && (
            <div className="stack-sm">
              <span className="field__label">How many</span>
              <input
                className="input"
                type="number"
                min={1}
                max={stock}
                value={quantity}
                onChange={(event) =>
                  setQuantity(Math.min(stock, Math.max(1, Number(event.target.value))))
                }
                style={{ width: 100 }}
              />
            </div>
          )}
          <div className="stack-sm">
            <span className="field__label">Listed for</span>
            <select
              className="select"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {durations.map((option) => (
                <option key={option} value={option}>
                  {option} day{option === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </div>
        </div>

        <input
          className="input"
          value={note}
          maxLength={140}
          placeholder="A note for shoppers (optional)"
          onChange={(event) => setNote(event.target.value)}
        />

        <div className="pxwell price-preview">
          <span className="tiny muted">
            {kind === 'ITEM' ? `${quantity} x ${formatNumber(price)}` : formatNumber(price)} asking
          </span>
          <span className="mono">
            you keep <strong>{formatNumber(take)}</strong> after the {fee}% fee
          </span>
        </div>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="gold" disabled={!picked || busy} loading={busy} onClick={() => void list()}>
            Put it out
          </Button>
        </div>
      </div>
    </Modal>
  );
}
